import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { CloseIcon, HamburgerIcon, PlusIcon, ExpandIcon, ContractIcon, TrashIcon, ClockIcon } from '@components/icons';
import { ChatMessageList } from './chat-message-list';
import { ChatInput } from './chat-input';
import { ConversationList } from './conversation-list';
import { CommentaryHistoryList } from './commentary-history-list';
import { AddToStudyCta } from './add-to-study-cta';
import { CommentaryToolbar } from './rich-message/commentary-toolbar';
import { commentaryView } from './commentary-view';
import { isDesktop } from '@/desktop/desktop';

export const ChatPanel = observer(function ChatPanel() {
  const [shouldRender, setShouldRender] = useState(false);
  const [animationState, setAnimationState] = useState<'entering' | 'exiting' | 'visible'>(
    'exiting',
  );

  const isVisible = bibleState.chatPanelVisible;
  const isUserDataOpen = bibleState.userDataPanelVisible;
  const isFullscreen = bibleState.chatFullscreen;
  const isSplitMode = isVisible && isUserDataOpen && !isFullscreen;
  const isListVisible = bibleState.isConversationListVisible;
  const isHistoryVisible = bibleState.isCommentaryHistoryVisible;
  // Actions for whatever commentary is on screen. Rendered outside the
  // scrolling list so they stay put as the reader scrolls.
  const view = commentaryView(bibleState.chatMessages);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setAnimationState('entering');
      const timer = setTimeout(() => setAnimationState('visible'), 400);
      return () => clearTimeout(timer);
    } else {
      setAnimationState('exiting');
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Eagerly load conversation list when panel opens
  useEffect(() => {
    if (isVisible && !bibleState.isConversationsLoaded) {
      bibleState.loadChatConversations();
    }
  }, [isVisible]);

  if (!shouldRender) return null;

  const sizeClass = isFullscreen
    ? 'chat-panel-fullscreen'
    : isSplitMode
      ? 'chat-panel-split'
      : 'chat-panel-full';
  const enterClass = animationState === 'entering' ? 'entering' : '';
  const exitClass = animationState === 'exiting' ? 'closing' : '';

  const headerBtnClass =
    '-m-1 rounded-full p-2 text-stone-400 transition-all hover:bg-black/5 hover:text-stone-600 dark:text-stone-500 dark:hover:bg-white/10 dark:hover:text-stone-300';

  return (
    <>
      {/* Fullscreen backdrop */}
      {isFullscreen && (
        <div
          className="chat-fullscreen-backdrop"
          onClick={() => bibleState.toggleChatFullscreen()}
        />
      )}

      <div className={`chat-panel ${sizeClass} ${enterClass} ${exitClass}`}>
        {/* Header */}
        <div className="chat-panel-header">
          <div className="flex min-w-0 items-center gap-2">
            <button
              onClick={() => bibleState.toggleConversationList()}
              className={headerBtnClass}
              title="Chat history"
            >
              <HamburgerIcon className="h-4 w-4" />
            </button>
            <span className="truncate text-lg font-semibold text-stone-800 dark:text-cyan-100">
              It is written...
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => bibleState.toggleCommentaryHistory()}
              className={headerBtnClass}
              title="Commentary history"
              data-testid="commentary-history-toggle"
            >
              <ClockIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => bibleState.clearChatHistory()}
              className={headerBtnClass}
              title="Clear"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => bibleState.startNewChatConversation()}
              className={headerBtnClass}
              title="New conversation"
            >
              <PlusIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => bibleState.toggleChatFullscreen()}
              className={headerBtnClass}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? (
                <ContractIcon className="h-4 w-4" />
              ) : (
                <ExpandIcon className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={() => bibleState.hideChatPanel()}
              className="-m-1 rounded-full p-2 text-stone-500 transition-all hover:bg-black/5 hover:text-stone-700 dark:text-stone-400 dark:hover:bg-white/10 dark:hover:text-stone-200"
              title="Close panel"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Pinned commentary actions — between the header and the scroll. */}
        {view && (
          <CommentaryToolbar
            commentary={view.commentary}
            reference={view.reference}
            pericopeTitle={view.pericopeTitle}
            reportInput={view.reportInput}
            originalQuery={view.originalQuery}
          />
        )}

        {/* Content area — messages with conversation list dropdown overlay */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <ChatMessageList />
          {isListVisible && (
            <>
              <div
                className="conversation-backdrop"
                onClick={() => bibleState.hideConversationList()}
              />
              <ConversationList />
            </>
          )}
          {isHistoryVisible && (
            <>
              <div
                className="conversation-backdrop"
                onClick={() => bibleState.hideCommentaryHistory()}
              />
              <CommentaryHistoryList />
            </>
          )}
        </div>

        {/* Sticky footer CTA — pericope commentary → current Study
            (Study system is native-only; no-op on web) */}
        {isDesktop() && <AddToStudyCta />}

        {/* Free-typed composer — only for user-initiated new chats (plus /
            fresh open); programmatic study flows keep it hidden. */}
        {bibleState.isChatComposerVisible && <ChatInput />}
      </div>
    </>
  );
});
